import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import { adminApi } from '../../api';
import { BASE_URL } from '../../api/client';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Button } from '../../components/Button';
import { formatCurrency } from '../../utils/currency';

interface PaymentDetails {
  payment_id: string;
  payment_reference: string;
  amount: string;
  payment_method: string;
  payment_date: string;
  bank_name: string;
  transaction_reference: string;
  payment_proof_url: string;
  notes?: string;
  status: string;
  loan_id: string;
  loan_reference: string;
  outstanding_principal: string;
  outstanding_interest: string;
  total_paid: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  created_at: string;
}

export const VerifyPayment: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [payment, setPayment] = useState<PaymentDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  useEffect(() => {
    if (!id) {
      navigate('/admin/payments/pending');
      return;
    }

    fetchPaymentDetails();
  }, [id]);

  const fetchPaymentDetails = async () => {
    try {
      const response = await adminApi.getPaymentById(id!);

      if (response.success && response.data) {
        setPayment(response.data.payment);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load payment details');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (isVerified: boolean) => {
    if (!isVerified && !verificationNotes.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await adminApi.verifyPayment(
        id!,
        isVerified,
        verificationNotes.trim() || undefined
      );

      if (response.success) {
        navigate('/admin/payments/pending', {
          state: {
            message: isVerified
              ? 'Payment verified successfully'
              : 'Payment rejected successfully',
          },
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to process verification');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!payment) {
    return (
      <div className="error-state">
        <p>Payment not found</p>
      </div>
    );
  }

  if (payment.status !== 'pending') {
    return (
      <div className="error-state">
        <p>This payment has already been {payment.status}</p>
        <Button onClick={() => navigate('/admin/payments/pending')} variant="primary">
          Back to Pending Payments
        </Button>
      </div>
    );
  }

  const totalOutstanding =
    parseFloat(payment.outstanding_principal) + parseFloat(payment.outstanding_interest);

  return (
    <div className="verify-payment-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Verify Payment</h1>
          <p>Review payment proof and approve or reject the transaction</p>
        </div>
        <Button onClick={() => navigate('/admin/payments/pending')} variant="secondary" icon={<ArrowLeft size={20} />}>
          Back to Pending Payments
        </Button>
      </div>

      {error && <ErrorMessage message={error} onClose={() => setError('')} />}

      <div className="verification-container">
        <div className="payment-details-section">
          <h2>Payment Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <span className="label">Payment Reference:</span>
              <span className="value">{payment.payment_reference}</span>
            </div>
            <div className="info-item">
              <span className="label">Amount:</span>
              <span className="value">
                {formatCurrency(payment.amount)}
              </span>
            </div>
            <div className="info-item">
              <span className="label">Payment Date:</span>
              <span className="value">{new Date(payment.payment_date).toLocaleDateString()}</span>
            </div>
            <div className="info-item">
              <span className="label">Submitted On:</span>
              <span className="value">{new Date(payment.created_at).toLocaleDateString()}</span>
            </div>
            <div className="info-item">
              <span className="label">Payment Method:</span>
              <span className="value">{payment.payment_method}</span>
            </div>
            <div className="info-item">
              <span className="label">Bank Name:</span>
              <span className="value">{payment.bank_name}</span>
            </div>
            <div className="info-item">
              <span className="label">Transaction Reference:</span>
              <span className="value">{payment.transaction_reference}</span>
            </div>
            {payment.notes && (
              <div className="info-item full-width">
                <span className="label">Customer Notes:</span>
                <span className="value">{payment.notes}</span>
              </div>
            )}
          </div>

          <div className="proof-section">
            <h3>Payment Proof</h3>
            {payment.payment_proof_url ? (
              <div className="proof-viewer">
                <img
                  src={`${BASE_URL}/${payment.payment_proof_url}`}
                  alt="Payment Proof"
                  style={{ maxWidth: '100%', maxHeight: '500px', objectFit: 'contain' }}
                />
                <Button
                  onClick={() => window.open(`${BASE_URL}/${payment.payment_proof_url}`, '_blank')}
                  variant="secondary"
                >
                  Open in New Tab
                </Button>
              </div>
            ) : (
              <p>No proof uploaded</p>
            )}
          </div>
        </div>

        <div className="loan-details-section">
          <h2>Loan Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <span className="label">Loan Reference:</span>
              <span className="value">{payment.loan_reference}</span>
            </div>
            <div className="info-item">
              <span className="label">Customer:</span>
              <span className="value">{payment.customer_name}</span>
            </div>
            <div className="info-item">
              <span className="label">Email:</span>
              <span className="value">{payment.customer_email}</span>
            </div>
            <div className="info-item">
              <span className="label">Phone:</span>
              <span className="value">{payment.customer_phone}</span>
            </div>
            <div className="info-item">
              <span className="label">Outstanding Principal:</span>
              <span className="value">
                {formatCurrency(payment.outstanding_principal)}
              </span>
            </div>
            <div className="info-item">
              <span className="label">Outstanding Interest:</span>
              <span className="value">
                {formatCurrency(payment.outstanding_interest)}
              </span>
            </div>
            <div className="info-item">
              <span className="label">Total Outstanding:</span>
              <span className="value">
                {formatCurrency(totalOutstanding)}
              </span>
            </div>
            <div className="info-item">
              <span className="label">Total Paid:</span>
              <span className="value">
                {formatCurrency(payment.total_paid)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {!showRejectForm ? (
        <div className="verification-actions">
          <div className="form-group">
            <label htmlFor="verification_notes">Verification Notes (Optional)</label>
            <textarea
              id="verification_notes"
              value={verificationNotes}
              onChange={(e) => setVerificationNotes(e.target.value)}
              disabled={submitting}
              rows={3}
              placeholder="Add any notes about this verification..."
            />
          </div>

          <div className="button-group">
            <Button
              onClick={() => setShowRejectForm(true)}
              variant="danger"
              disabled={submitting}
              icon={<XCircle size={20} />}
            >
              Reject Payment
            </Button>
            <Button
              onClick={() => handleVerify(true)}
              variant="success"
              disabled={submitting}
              icon={<CheckCircle size={20} />}
              loading={submitting}
            >
              Approve Payment
            </Button>
          </div>
        </div>
      ) : (
        <div className="reject-form">
          <h3>Reject Payment</h3>
          <p>Please provide a reason for rejecting this payment:</p>
          <div className="form-group">
            <label htmlFor="reject_reason">Rejection Reason *</label>
            <textarea
              id="reject_reason"
              value={verificationNotes}
              onChange={(e) => setVerificationNotes(e.target.value)}
              disabled={submitting}
              rows={4}
              placeholder="Enter reason for rejection..."
              required
            />
          </div>
          <div className="button-group">
            <Button
              onClick={() => {
                setShowRejectForm(false);
                setVerificationNotes('');
              }}
              variant="secondary"
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleVerify(false)}
              variant="danger"
              disabled={submitting || !verificationNotes.trim()}
              loading={submitting}
            >
              Confirm Rejection
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
